import { AppStoreLinks } from '@/components/LoginPage/AppStoreLinks'
import { LoginBackdrop } from '@/components/LoginPage/LoginBackdrop'
import { LoginForm } from '@/components/LoginPage/LoginForm'
import Logo, { LogoColorVariant } from '@/components/svgs/Logo'
import { useAuthStore } from '@/store'
import { AuthLoginState } from '@/store/auth/AuthenticationStore'
import { FlowHeaderIcon } from '@/ui/core/flow-header'
import { Pressable } from '@/ui/core/pressable'
import { Screen } from '@/ui/core/screen'
import { VStack } from '@/ui/core/stack'
import { cn } from '@/ui/utils'
import { useEffect, useState } from 'react'
import { View, useWindowDimensions } from 'react-native'
import Animated, {
	useAnimatedStyle,
	useSharedValue,
	withTiming,
} from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

/** The wordmark's proportions, so a full-width mark gets the right height. */
const WORDMARK_RATIO = 488 / 1562
const PAGE_INSET = 20
const MASTHEAD_TOP = 16
/** On the pink steps the mark takes the flow header's exact geometry: 84pt wide in a 56pt bar. */
const HEADER_MARK_WIDTH = 84
const HEADER_BAR_HEIGHT = 56

/**
 * The front door in three steps. The welcome's wordmark runs edge to edge across the top; once
 * the form arrives it eases down into the same small mark and bar every other screen has, with
 * the back ring on the left of that bar. The welcome is the photograph, status bar to home indicator, with one Log In
 * button held at the bottom. Tapping it dissolves the photograph and the form takes the pink from
 * the top, as forms do: Apple, Google, or an email, then the code. A back ring
 * under the mark returns to the welcome. When the keyboard comes up the form rides above it.
 */
export default function LoginScreen() {
	const insets = useSafeAreaInsets()
	const { width } = useWindowDimensions()
	const [started, setStarted] = useState(false)
	const { loginState } = useAuthStore()
	const codeSent = loginState === AuthLoginState.CODE_SENT
	const onPhoto = !started && !codeSent
	const canGoBack = started && !codeSent
	const markWidth = Math.min(width, 600) - PAGE_INSET * 2
	const markHeight = markWidth * WORDMARK_RATIO

	// The masthead eases down into the header's geometry as the form arrives, and back up on the
	// way out. 0 is the welcome's edge-to-edge mark; 1 is the small mark every other screen has.
	const headerScale = HEADER_MARK_WIDTH / markWidth
	const headerMarkHeight = HEADER_MARK_WIDTH * WORDMARK_RATIO
	const progress = useSharedValue(onPhoto ? 0 : 1)
	useEffect(() => {
		progress.value = withTiming(onPhoto ? 0 : 1, { duration: 260 })
	}, [onPhoto, progress])
	const markBoxStyle = useAnimatedStyle(() => ({
		// The welcome's mark sits 16pt under the safe area; the header's bar starts right at it.
		marginTop: MASTHEAD_TOP * (1 - progress.value),
		height: markHeight + (HEADER_BAR_HEIGHT - markHeight) * progress.value,
	}))
	const markStyle = useAnimatedStyle(() => ({
		transform: [
			{ translateY: ((HEADER_BAR_HEIGHT - headerMarkHeight) / 2) * progress.value },
			{ scale: 1 + (headerScale - 1) * progress.value },
		],
		transformOrigin: 'top center',
	}))
	const ringStyle = useAnimatedStyle(() => ({ opacity: progress.value }))

	return (
		<Screen
			preset="fixed"
			className="flex flex-1"
			StatusBarProps={{ style: onPhoto ? 'light' : 'dark' }}
		>
			{onPhoto && <LoginBackdrop />}
			<View className="items-center px-5" style={{ paddingTop: insets.top }}>
				<Animated.View className="w-full items-center" style={markBoxStyle}>
					<Animated.View style={markStyle}>
						<Logo
							color={onPhoto ? LogoColorVariant.LIGHT : LogoColorVariant.DARK}
							width={markWidth}
							height={markHeight}
						/>
					</Animated.View>
					{canGoBack && (
						<Animated.View
							className="absolute left-0"
							style={[{ top: (HEADER_BAR_HEIGHT - 44) / 2 }, ringStyle]}
						>
							<Pressable
								onPress={() => setStarted(false)}
								accessibilityRole="button"
								accessibilityLabel="Back"
								hitSlop={6}
								className="h-11 w-11 items-center justify-center rounded-full border border-ink"
							>
								<FlowHeaderIcon name="arrow-back" />
							</Pressable>
						</Animated.View>
					)}
				</Animated.View>
			</View>
			<VStack
				className={cn(
					'flex-1 lg:justify-center lg:items-center',
					onPhoto ? 'justify-end' : 'justify-start'
				)}
			>
				<View
					className={cn('w-full px-5 lg:max-w-[480px]', onPhoto ? 'pt-6' : 'pt-12')}
					style={{ paddingBottom: Math.max(insets.bottom, 16) + 8 }}
				>
					<LoginForm started={started} onStart={() => setStarted(true)} />
					<AppStoreLinks />
				</View>
			</VStack>
		</Screen>
	)
}
